import { Mail, MessageSquare, Phone, Plus, Trash2, Zap } from "lucide-react";
import { useState } from "react";
import {
  useCreate,
  useDelete,
  useGetIdentity,
  useGetList,
  useGetMany,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { EmptyState } from "../data-display/EmptyState";
import { SectionCard } from "../data-display/SectionCard";
import { StatusBadge } from "../data-display/StatusBadge";
import { ListPageHeader } from "../list/ListPageHeader";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import {
  ENROLLMENT_STATUS_LABELS,
  ENROLLMENT_STATUS_TONES,
  STEP_CHANNELS,
  stepChannelLabel,
  type Sequence,
  type SequenceEnrollment,
  type SequenceStep,
} from "./sequenceModel";

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

const CHANNEL_ICONS: Record<string, typeof Mail> = {
  email: Mail,
  sms: MessageSquare,
  call: Phone,
  task: Zap,
};

/**
 * Automated follow-up sequences (Starter tier). The database engine runs
 * every minute and materializes due steps as follow-up tasks with rendered
 * drafts; when the communications layer ships, email/sms steps upgrade to
 * real sends through channel adapters.
 */
export const SequencesPage = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const refresh = useRefresh();
  const notify = useNotify();
  const [create, { isPending: creating }] = useCreate();
  const [newName, setNewName] = useState("");

  const { data: sequences, isPending } = useGetList<Sequence>("sequences", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "ASC" },
  });

  const selected =
    sequences?.find((s) => s.id === selectedId) ?? sequences?.[0] ?? null;

  const createSequence = () => {
    if (!newName.trim()) {
      notify("Name the sequence first", { type: "warning" });
      return;
    }
    create(
      "sequences",
      {
        data: { name: newName.trim(), workspace_id: getActiveWorkspaceId() },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: (record: Sequence) => {
          notify("Sequence created");
          setNewName("");
          setSelectedId(record.id);
          refresh();
        },
        onError: (error: unknown) =>
          notify(error instanceof Error ? error.message : "Could not create", {
            type: "error",
          }),
      },
    );
  };

  if (isPending) return null;

  return (
    <>
      <ListPageHeader
        title="Sequences"
        subtitle="Automated follow-up cadences, each due step lands as an assigned task with a ready-to-send draft"
        actions={
          <div className="flex items-center gap-2">
            <input
              className={cn(inputClass, "w-56")}
              placeholder="New sequence name…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createSequence()}
            />
            <Button size="sm" disabled={creating} onClick={createSequence}>
              <Plus className="mr-1 size-3.5" />
              Create
            </Button>
          </div>
        }
      />

      {(sequences ?? []).length === 0 ? (
        <Card className="py-16">
          <EmptyState
            icon={Zap}
            title="No sequences yet"
            description="Create a follow-up cadence and enroll contacts, Gravun schedules every touch for you."
          />
        </Card>
      ) : (
        <div className="grid gap-4 [&>*]:min-w-0 lg:grid-cols-[280px_1fr]">
          <Card className="h-fit overflow-hidden py-0">
            {(sequences ?? []).map((sequence) => (
              <button
                key={sequence.id}
                type="button"
                onClick={() => setSelectedId(sequence.id)}
                className={cn(
                  "flex w-full items-center justify-between gap-2 border-b border-border-subtle px-4 py-3 text-left text-[13.5px] transition-colors last:border-0",
                  selected?.id === sequence.id
                    ? "bg-surface-muted font-medium text-foreground"
                    : "text-muted-foreground hover:bg-surface-muted/60",
                )}
              >
                <span className="truncate">{sequence.name}</span>
                <StatusBadge
                  label={sequence.active ? "Active" : "Paused"}
                  tone={sequence.active ? "success" : "neutral"}
                />
              </button>
            ))}
          </Card>

          {selected ? <SequenceDetail sequence={selected} /> : null}
        </div>
      )}
    </>
  );
};

const SequenceDetail = ({ sequence }: { sequence: Sequence }) => {
  const refresh = useRefresh();
  const [update] = useUpdate();

  const toggleActive = () =>
    update(
      "sequences",
      {
        id: sequence.id,
        data: { active: !sequence.active },
        previousData: sequence,
      },
      { mutationMode: "pessimistic", onSuccess: () => refresh() },
    );

  const toggleExitOnReply = () =>
    update(
      "sequences",
      {
        id: sequence.id,
        data: { exit_on_reply: !sequence.exit_on_reply },
        previousData: sequence,
      },
      { mutationMode: "pessimistic", onSuccess: () => refresh() },
    );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex items-center justify-between gap-3 py-4">
          <div>
            <h2 className="text-[16px] font-semibold">{sequence.name}</h2>
            {sequence.description ? (
              <p className="text-[13px] text-muted-foreground">
                {sequence.description}
              </p>
            ) : null}
          </div>
          <Button variant="outline" size="sm" onClick={toggleActive}>
            {sequence.active ? "Pause sequence" : "Resume sequence"}
          </Button>
        </CardContent>
        <CardContent className="border-t border-border pt-3">
          <label className="flex items-start gap-2 text-[12.5px]">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={sequence.exit_on_reply}
              onChange={toggleExitOnReply}
            />
            <span>
              Stop when the customer replies
              <span className="block text-[11.5px] text-muted-foreground">
                A reply on any channel ends the follow-up. Leaving this off
                means someone who already answered still gets the remaining
                steps.
              </span>
            </span>
          </label>
        </CardContent>
      </Card>
      <StepsEditor sequence={sequence} />
      <EnrollmentsPanel sequence={sequence} />
    </div>
  );
};

const StepsEditor = ({ sequence }: { sequence: Sequence }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create, { isPending }] = useCreate();
  const [deleteOne] = useDelete();
  const [delay, setDelay] = useState("0");
  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const { data: steps } = useGetList<SequenceStep>("sequence_steps", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "position", order: "ASC" },
    filter: { sequence_id: sequence.id },
  });

  const addStep = () => {
    if (!body.trim()) {
      notify("Write the step message first", { type: "warning" });
      return;
    }
    create(
      "sequence_steps",
      {
        data: {
          sequence_id: sequence.id,
          position: steps?.length ?? 0,
          delay_days: Number(delay) || 0,
          channel,
          subject: subject.trim() || null,
          body: body.trim(),
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("Step added");
          setSubject("");
          setBody("");
          refresh();
        },
        onError: (error: unknown) =>
          notify(
            error instanceof Error ? error.message : "Could not add the step",
            { type: "error" },
          ),
      },
    );
  };

  return (
    <SectionCard
      title="Steps"
      subtitle="Delays are measured from the previous step. Use {{first_name}}, {{last_name}} and {{company}} in drafts."
    >
      <div className="flex flex-col gap-2 px-1">
        {(steps ?? []).map((step, index) => {
          const Icon = CHANNEL_ICONS[step.channel] ?? Zap;
          return (
            <div
              key={step.id}
              className="flex items-start gap-3 rounded-md border border-border-subtle p-3"
            >
              <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon className="size-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium">
                  {index + 1}. {stepChannelLabel(step.channel)}
                  <span className="ml-2 font-normal text-muted-foreground">
                    {step.delay_days === 0
                      ? index === 0
                        ? "immediately"
                        : "same day"
                      : `after ${step.delay_days} day${step.delay_days === 1 ? "" : "s"}`}
                  </span>
                </p>
                {step.subject ? (
                  <p className="truncate text-[13px] text-foreground">
                    {step.subject}
                  </p>
                ) : null}
                <p className="line-clamp-2 text-[12.5px] text-muted-foreground">
                  {step.body}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-muted-foreground hover:text-destructive"
                onClick={() =>
                  deleteOne(
                    "sequence_steps",
                    { id: step.id, previousData: step },
                    {
                      mutationMode: "pessimistic",
                      onSuccess: () => refresh(),
                    },
                  )
                }
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          );
        })}
        {(steps ?? []).length === 0 ? (
          <p className="py-1 text-[13px] text-muted-foreground">
            No steps yet, add the first touch below.
          </p>
        ) : null}

        <div className="mt-1 flex flex-col gap-2 border-t border-border-subtle pt-3">
          <div className="flex gap-2">
            <label className="flex w-28 flex-col gap-1 text-[12.5px] font-medium">
              Delay (days)
              <input
                className={inputClass}
                type="number"
                min="0"
                value={delay}
                onChange={(e) => setDelay(e.target.value)}
              />
            </label>
            <label className="flex w-36 flex-col gap-1 text-[12.5px] font-medium">
              Channel
              <select
                className={inputClass}
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
              >
                {STEP_CHANNELS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-1 flex-col gap-1 text-[12.5px] font-medium">
              Subject (email only)
              <input
                className={inputClass}
                placeholder="Thanks for reaching out, {{first_name}}"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </label>
          </div>
          <textarea
            className="min-h-20 w-full rounded-md border border-input bg-transparent px-2.5 py-1.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="Hi {{first_name}}, following up on…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Button
            size="sm"
            className="w-fit"
            disabled={isPending}
            onClick={addStep}
          >
            <Plus className="mr-1 size-3.5" />
            Add step
          </Button>
        </div>
      </div>
    </SectionCard>
  );
};

const EnrollmentsPanel = ({ sequence }: { sequence: Sequence }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const [create, { isPending }] = useCreate();
  const [update] = useUpdate();
  const [contactId, setContactId] = useState("");

  const { data: enrollments } = useGetList<SequenceEnrollment>(
    "sequence_enrollments",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "enrolled_at", order: "DESC" },
      filter: { sequence_id: sequence.id },
    },
  );

  const { data: contacts } = useGetList<{
    id: number;
    first_name: string;
    last_name: string;
  }>("contacts_summary", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "first_name", order: "ASC" },
  });

  const { data: enrolledContacts } = useGetMany(
    "contacts_summary",
    { ids: (enrollments ?? []).map((e) => e.contact_id) },
    { enabled: (enrollments ?? []).length > 0 },
  );

  const contactName = (id: number) => {
    const c = (enrolledContacts ?? []).find((x) => x.id === id) as
      | { first_name: string; last_name: string }
      | undefined;
    return c ? `${c.first_name} ${c.last_name}` : `#${id}`;
  };

  const enroll = () => {
    if (!contactId) return;
    create(
      "sequence_enrollments",
      {
        data: {
          sequence_id: sequence.id,
          contact_id: Number(contactId),
          enrolled_by: identity?.id ?? null,
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("Contact enrolled, first touch scheduled");
          setContactId("");
          refresh();
        },
        onError: (error: unknown) =>
          notify(
            error instanceof Error
              ? error.message
              : "Could not enroll the contact",
            { type: "error" },
          ),
      },
    );
  };

  const cancel = (enrollment: SequenceEnrollment) =>
    update(
      "sequence_enrollments",
      {
        id: enrollment.id,
        data: { status: "cancelled", next_run_at: null },
        previousData: enrollment,
      },
      { mutationMode: "pessimistic", onSuccess: () => refresh() },
    );

  return (
    <SectionCard title="Enrollments">
      <div className="flex flex-col gap-2 px-1">
        <div className="flex items-end gap-2">
          <label className="flex flex-1 flex-col gap-1 text-[12.5px] font-medium">
            Enroll a contact
            <select
              className={inputClass}
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
            >
              <option value="">Choose a contact…</option>
              {(contacts ?? []).map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.first_name} {contact.last_name}
                </option>
              ))}
            </select>
          </label>
          <Button size="sm" disabled={isPending || !contactId} onClick={enroll}>
            Enroll
          </Button>
        </div>

        {(enrollments ?? []).map((enrollment) => (
          <div
            key={enrollment.id}
            className="flex items-center gap-3 rounded-md border border-border-subtle px-3 py-2 text-[13px]"
          >
            <span className="min-w-0 flex-1 truncate font-medium">
              {contactName(enrollment.contact_id)}
            </span>
            <span className="text-muted-foreground">
              Step {enrollment.current_step}
              {enrollment.next_run_at
                ? ` · next ${new Date(enrollment.next_run_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
                : ""}
            </span>
            <StatusBadge
              label={
                ENROLLMENT_STATUS_LABELS[enrollment.status] ?? enrollment.status
              }
              tone={ENROLLMENT_STATUS_TONES[enrollment.status]}
            />
            {enrollment.status === "active" ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-[12px] text-muted-foreground hover:text-destructive"
                onClick={() => cancel(enrollment)}
              >
                Stop
              </Button>
            ) : null}
          </div>
        ))}
        {(enrollments ?? []).length === 0 ? (
          <p className="py-1 text-[13px] text-muted-foreground">
            Nobody enrolled yet.
          </p>
        ) : null}
      </div>
    </SectionCard>
  );
};
